import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ChangeDetectionStrategy, Component, computed, effect, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { NgxEchartsDirective, provideEchartsCore } from "ngx-echarts";
import * as echarts from "echarts";
import type { EChartsOption } from "echarts";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { S25Assert } from "../../../util/s25.assert";
import { PreferenceService } from "../../../services/preference.service";
import { StateService } from "../../../services/state.service";
import { CustomAttributeService } from "../../../services/custom.attribute.service";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { S25TriggerRerenderModule } from "../../s25-trigger-rerender/s25.trigger.rerender.module";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Nullable } from "../../../pojo/Util";
import {
    CategorySlice,
    ContactCustomAttributeDef,
    DemographicBreakdown,
    EngagementAnalyticsFilter,
    MembershipBreakdown,
    MembershipCountPoint,
    RankedItem,
    SeAnalyticsService,
    TimelinePoint,
} from "./se.analytics.service";
import {
    aggregateSlicesIntoBuckets,
    bucketRangeHint,
    comparableToInputValue,
    DemographicBucketDef,
    emptyBucketDef,
    getDemographicBucketKind,
    hasHighCardinality,
    hasValidBucketLabels,
    isBucketizableDemographicType,
    parseBucketInputValue,
    suggestEqualBuckets,
    validateBuckets,
} from "./demographic.bucket.util";
import { Item } from "../../../pojo/Item";

type Bucket = "day" | "week" | "month";
type Metric = "total" | "unique";

/** Global id-list filters, each backed by an item-store picker and round-tripped through the URL. */
type PickerKind = "orgs" | "cats" | "events" | "locs";

function idsOf(items: DropDownItem[]): number[] {
    return items.map((item) => Number(item.itemId)).filter((id) => !isNaN(id));
}

/** Initial query params: prefer the ui-router hash query (production), fall back to the raw search (tests). */
function readSeed(): URLSearchParams {
    const routerSearch = StateService.getSearch();
    if (Object.keys(routerSearch).length) {
        return new URLSearchParams(routerSearch);
    }
    return new URLSearchParams(window.location.search);
}

function idsToParam(ids: number[]): string | undefined {
    return ids.length ? ids.join(",") : undefined;
}

interface WidgetDef {
    key: string;
    title: string;
}

interface WidgetConfig {
    order: string[];
    hidden: Record<string, boolean>;
}

const WIDGET_PREF = "seAnalyticsWidgets";

const ALL_WIDGETS: WidgetDef[] = [
    { key: "summary", title: "Summary" },
    { key: "timeline", title: "Attendance Over Time" },
    { key: "topOrganizations", title: "Top Organizations" },
    { key: "topEvents", title: "Top Events" },
    { key: "topLocations", title: "Top Locations" },
    { key: "categories", title: "Categories" },
    { key: "membershipOverTime", title: "Membership Over Time" },
    { key: "membership", title: "Attendance by Membership" },
    { key: "demographics", title: "Demographics" },
];

const PALETTE = [
    "#4e79a7",
    "#f28e2b",
    "#e15759",
    "#76b7b2",
    "#59a14f",
    "#edc948",
    "#b07aa1",
    "#ff9da7",
    "#9c755f",
    "#bab0ac",
];

/** Base + lighter tint pairs for zebra-striped bar charts */
const BAR_COLOR_PAIR = { primary: PALETTE[0], alternate: "#7ba3c4" } as const;
const BAR_COLOR_PAIR_TEAL = { primary: PALETTE[3], alternate: "#a8d4d0" } as const;

function barColorForIndex(dataIndex: number, pair: { primary: string; alternate: string }): string {
    return dataIndex % 2 === 0 ? pair.primary : pair.alternate;
}

function isoDate(date: Date): string {
    return date.toISOString().slice(0, 10);
}

function metricValue(item: { total: number; unique: number }, metric: Metric): number {
    return metric === "unique" ? item.unique : item.total;
}

function rankedBarOption(items: RankedItem[], metric: Metric): EChartsOption {
    if (!items.length) {
        return { title: { text: "No data", left: "center", top: "middle" } };
    }
    const sorted = [...items].sort((first, second) => metricValue(first, metric) - metricValue(second, metric));
    return {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        grid: { left: "3%", right: "6%", bottom: "3%", containLabel: true },
        xAxis: { type: "value" },
        yAxis: { type: "category", data: sorted.map((item) => item.name ?? `#${item.id ?? "?"}`) },
        series: [
            {
                type: "bar",
                data: sorted.map((item) => metricValue(item, metric)),
                itemStyle: {
                    color: (params) => barColorForIndex(params.dataIndex, BAR_COLOR_PAIR),
                },
                label: { show: true, position: "right" },
            },
        ],
    };
}

function scrollableAxisTooltip(): EChartsOption["tooltip"] {
    return {
        trigger: "axis",
        enterable: true,
        confine: true,
        order: "valueDesc",
        hideDelay: 100,
        extraCssText: "max-height: 60vh; overflow-y: auto; white-space: normal;",
        position(point, _params, _dom, _rect, size) {
            const rightMargin = 12;
            const topMargin = 12;
            const boxWidth = size.contentSize[0];
            const boxHeight = size.contentSize[1];
            const left = size.viewSize[0] - boxWidth - rightMargin;
            const top = Math.min(
                Math.max(point[1] - boxHeight / 2, topMargin),
                size.viewSize[1] - boxHeight - topMargin,
            );
            return [left, top];
        },
    };
}

function pieOption(slices: { name: string; total: number; unique: number }[], metric: Metric): EChartsOption {
    const data = slices
        .map((slice) => ({ name: slice.name, value: metricValue(slice, metric) }))
        .filter((entry) => entry.value > 0);
    if (!data.length) {
        return { title: { text: "No data", left: "center", top: "middle" } };
    }
    return {
        tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)" },
        legend: { type: "scroll", bottom: 0 },
        series: [
            {
                type: "pie",
                radius: ["35%", "65%"],
                data,
            },
        ],
    };
}

function membershipOverTimeOption(points: MembershipCountPoint[]): EChartsOption {
    if (!points.length) {
        return { title: { text: "No data", left: "center", top: "middle" } };
    }
    const bucketSet = new Set<string>();
    for (const point of points) bucketSet.add(point.bucket);
    const categories = [...bucketSet].sort();

    const countsByOrg = new Map<number, { name: string; countsByBucket: Map<string, number> }>();
    for (const point of points) {
        const orgId = point.orgId ?? 0;
        let orgEntry = countsByOrg.get(orgId);
        if (!orgEntry) {
            orgEntry = {
                name: point.orgName ?? `Organization ${orgId}`,
                countsByBucket: new Map(),
            };
            countsByOrg.set(orgId, orgEntry);
        }
        orgEntry.countsByBucket.set(point.bucket, point.contCount);
    }

    const orgEntries = [...countsByOrg.values()].sort((first, second) =>
        first.name.localeCompare(second.name, undefined, { sensitivity: "base" }),
    );
    const series = orgEntries.map((orgEntry, idx) => ({
        name: orgEntry.name,
        type: "line" as const,
        smooth: true,
        data: categories.map((bucket) => orgEntry.countsByBucket.get(bucket) ?? "-"),
        itemStyle: { color: PALETTE[idx % PALETTE.length] },
    }));

    return {
        tooltip: scrollableAxisTooltip(),
        legend: { type: "scroll", bottom: 0 },
        grid: { left: "3%", right: "4%", bottom: "12%", containLabel: true },
        xAxis: { type: "category", boundaryGap: false, data: categories },
        yAxis: { type: "value", name: "Members" },
        series,
    };
}

function timelineOption(points: TimelinePoint[]): EChartsOption {
    if (!points.length) {
        return { title: { text: "No data", left: "center", top: "middle" } };
    }
    return {
        tooltip: { trigger: "axis" },
        legend: { bottom: 0, data: ["Total", "Unique"] },
        grid: { left: "3%", right: "4%", bottom: "12%", containLabel: true },
        xAxis: { type: "category", boundaryGap: true, data: points.map((point) => point.bucket) },
        yAxis: { type: "value", name: "Attendees" },
        series: [
            {
                name: "Total",
                type: "bar",
                data: points.map((point) => point.total),
                itemStyle: {
                    color: (params) => barColorForIndex(params.dataIndex, BAR_COLOR_PAIR),
                },
            },
            {
                name: "Unique",
                type: "bar",
                data: points.map((point) => point.unique),
                itemStyle: {
                    color: (params) => barColorForIndex(params.dataIndex, BAR_COLOR_PAIR_TEAL),
                },
            },
        ],
    };
}

@TypeManagerDecorator("s25-ng-se-analytics-dashboard")
@Component({
    selector: "s25-ng-se-analytics-dashboard",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, NgxEchartsDirective, S25DropdownPaginatedModule, S25TriggerRerenderModule],
    providers: [provideEchartsCore({ echarts })],
    template: `
        <div class="se-analytics">
            <h2 class="se-analytics__title">Engagement Analytics</h2>

            <div class="se-analytics__controls">
                <label class="se-analytics__field">
                    <span>Start</span>
                    <input class="c-input" type="date" [ngModel]="startDt()" (ngModelChange)="startDt.set($event)" />
                </label>
                <label class="se-analytics__field">
                    <span>End</span>
                    <input class="c-input" type="date" [ngModel]="endDt()" (ngModelChange)="endDt.set($event)" />
                </label>
                <label class="se-analytics__field">
                    <span>Group By</span>
                    <select class="c-input" [ngModel]="bucket()" (ngModelChange)="onBucketChange($event)">
                        <option value="day">Day</option>
                        <option value="week">Week</option>
                        <option value="month">Month</option>
                    </select>
                </label>
                <label class="se-analytics__field">
                    <span>Metric</span>
                    <select class="c-input" [ngModel]="metric()" (ngModelChange)="onMetricChange($event)">
                        <option value="total">Total Attendees</option>
                        <option value="unique">Unique Attendees</option>
                    </select>
                </label>
                <label
                    class="se-analytics__field se-analytics__field--checkbox"
                    [title]="
                        selectedCaCount()
                            ? 'Exclude attendees whose demographic value is the (None) label'
                            : 'Select at least one demographic attribute below'
                    "
                >
                    <input
                        type="checkbox"
                        class="c-input"
                        [checked]="excludeNone()"
                        [disabled]="!selectedCaCount()"
                        (change)="onExcludeNoneChange($any($event.target).checked)"
                    />
                    <span>Exclude '(None)' Label</span>
                </label>
                <div class="se-analytics__actions">
                    <button
                        type="button"
                        class="aw-button aw-button--outline"
                        (click)="reload()"
                        [disabled]="loading()"
                    >
                        Refresh
                    </button>
                    <button type="button" class="aw-button aw-button--danger--outline" (click)="clearFilters()">
                        Clear Filters
                    </button>
                </div>
            </div>

            <div class="se-analytics__filters" *s25-ng-trigger-rerender="pickersRemountKey()">
                <label class="se-analytics__field se-analytics__field--wide">
                    <span>Organizations</span>
                    <s25-ng-dropdown-multi-search-criteria
                        [type]="'studentEngagementOrganizations'"
                        [value]="orgsModel()"
                        [hasDone]="false"
                        [placeholder]="'All organizations'"
                        [ariaLabel]="'Filter by organization'"
                        (done)="commitPicker('orgs', $event)"
                    />
                </label>
                <label class="se-analytics__field se-analytics__field--wide">
                    <span>Event Categories</span>
                    <s25-ng-dropdown-multi-search-criteria
                        [type]="'eventCategories'"
                        [value]="eventCategoriesModel()"
                        [hasDone]="false"
                        [placeholder]="'All categories'"
                        [ariaLabel]="'Filter by event category'"
                        (done)="commitPicker('cats', $event)"
                    />
                </label>
                <label class="se-analytics__field se-analytics__field--wide">
                    <span>Events</span>
                    <s25-ng-dropdown-multi-search-criteria
                        [type]="'events'"
                        [value]="eventsModel()"
                        [hasDone]="false"
                        [placeholder]="'All events'"
                        [ariaLabel]="'Filter by event'"
                        (done)="commitPicker('events', $event)"
                    />
                </label>
                <label class="se-analytics__field se-analytics__field--wide">
                    <span>Locations</span>
                    <s25-ng-dropdown-multi-search-criteria
                        [type]="'locations'"
                        [value]="locationsModel()"
                        [hasDone]="false"
                        [placeholder]="'All locations'"
                        [ariaLabel]="'Filter by location'"
                        (done)="commitPicker('locs', $event)"
                    />
                </label>
            </div>

            <details class="se-analytics__demographics-panel">
                <summary class="se-analytics__demographics-summary">
                    Demographics (contact attributes)
                    @if (selectedCaCount()) {
                        <span class="se-analytics__demographics-count">{{ selectedCaCount() }} selected</span>
                    }
                </summary>
                @if (contactCasRes.value()?.length) {
                    <div class="se-analytics__demographics-options">
                        @for (ca of contactCasRes.value(); track ca.custAtrbId) {
                            <label class="se-analytics__demographics-option">
                                <input
                                    type="checkbox"
                                    class="c-input"
                                    [checked]="isCaSelected(ca.custAtrbId)"
                                    (change)="toggleCa(ca.custAtrbId)"
                                />
                                <span>{{ ca.custAtrbName }}</span>
                            </label>
                        }
                    </div>
                } @else {
                    <p class="se-analytics__muted se-analytics__demographics-empty">No contact attributes available.</p>
                }
            </details>

            <div class="se-analytics__widget-toggles">
                @for (widget of allWidgets; track widget.key) {
                    <label class="se-analytics__chip">
                        <input
                            type="checkbox"
                            class="c-input"
                            [checked]="!isHidden(widget.key)"
                            (change)="toggleWidget(widget.key)"
                        />
                        {{ widget.title }}
                    </label>
                }
            </div>

            @if (loading()) {
                <p class="se-analytics__muted">Loading…</p>
            } @else if (errorMessage()) {
                <p class="se-analytics__error" role="alert">{{ errorMessage() }}</p>
            } @else {
                @for (key of visibleWidgets(); track key) {
                    <section class="se-analytics__widget">
                        <header class="se-analytics__widget-header">
                            <h3>{{ titleFor(key) }}</h3>
                            <div class="se-analytics__widget-actions">
                                <button type="button" class="c-textButton" (click)="moveUp(key)">↑</button>
                                <button type="button" class="c-textButton" (click)="moveDown(key)">↓</button>
                                <button type="button" class="c-textButton" (click)="exportReport(key)">
                                    Export CSV
                                </button>
                            </div>
                        </header>

                        @if (key === "summary") {
                            <div class="se-analytics__cards">
                                <div class="se-analytics__card">
                                    <div class="se-analytics__card-value">{{ summary().activeOrganizations }}</div>
                                    <div class="se-analytics__card-label">Active Organizations</div>
                                </div>
                                <div class="se-analytics__card">
                                    <div class="se-analytics__card-value">{{ summary().attendedEvents }}</div>
                                    <div class="se-analytics__card-label">Events</div>
                                </div>
                                <div class="se-analytics__card">
                                    <div class="se-analytics__card-value">{{ summary().totalAttendees }}</div>
                                    <div class="se-analytics__card-label">Total Attendees</div>
                                </div>
                                <div class="se-analytics__card">
                                    <div class="se-analytics__card-value">{{ summary().uniqueAttendees }}</div>
                                    <div class="se-analytics__card-label">Unique Attendees</div>
                                </div>
                                <div class="se-analytics__card">
                                    <div class="se-analytics__card-value">{{ summary().averageAttendees }}</div>
                                    <div class="se-analytics__card-label">Avg / Event</div>
                                </div>
                            </div>
                        } @else if (key === "demographics") {
                            @if (demographics().length) {
                                <div class="se-analytics__dimensions">
                                    @for (dim of demographics(); track dim.custAtrbId) {
                                        <button
                                            type="button"
                                            class="se-analytics__dimension"
                                            [class.se-analytics__dimension--active]="
                                                activeDemographic() === dim.custAtrbId
                                            "
                                            (click)="activeDemographic.set(dim.custAtrbId ?? null)"
                                        >
                                            {{ dim.name }}
                                        </button>
                                    }
                                </div>
                                @if (showBucketizer()) {
                                    <details class="se-analytics__bucket-panel" open>
                                        <summary class="se-analytics__bucket-summary">Bucket ranges</summary>
                                        <p class="se-analytics__bucket-hint se-analytics__muted">
                                            {{ bucketRangeHintText() }}
                                        </p>
                                        @if (showSuggestBucketsPrompt()) {
                                            <p class="se-analytics__muted">
                                                Many distinct values — use Suggest equal ranges to get started.
                                            </p>
                                        }
                                        @if (activeBucketValidationError()) {
                                            <p class="se-analytics__error" role="alert">
                                                {{ activeBucketValidationError() }}
                                            </p>
                                        }
                                        <div class="se-analytics__bucket-rows">
                                            @for (
                                                bucket of activeDemographicBuckets();
                                                track $index;
                                                let bucketIndex = $index
                                            ) {
                                                <div class="se-analytics__bucket-row">
                                                    <label class="se-analytics__bucket-field">
                                                        <span>Label</span>
                                                        <input
                                                            class="c-input"
                                                            type="text"
                                                            [ngModel]="bucket.label"
                                                            (ngModelChange)="
                                                                updateActiveBucket(bucketIndex, { label: $event })
                                                            "
                                                        />
                                                    </label>
                                                    <label class="se-analytics__bucket-field">
                                                        <span>Min</span>
                                                        @if (activeBucketKind() === "numeric") {
                                                            <input
                                                                class="c-input"
                                                                type="number"
                                                                step="any"
                                                                [ngModel]="bucket.min"
                                                                (ngModelChange)="
                                                                    updateActiveBucket(bucketIndex, {
                                                                        min: parseBucketBound($event),
                                                                    })
                                                                "
                                                            />
                                                        } @else if (activeBucketKind() === "date") {
                                                            <input
                                                                class="c-input"
                                                                type="date"
                                                                [ngModel]="bucketBoundInput(bucket.min)"
                                                                (ngModelChange)="
                                                                    updateActiveBucket(bucketIndex, {
                                                                        min: parseBucketBound($event),
                                                                    })
                                                                "
                                                            />
                                                        } @else if (activeBucketKind() === "datetime") {
                                                            <input
                                                                class="c-input"
                                                                type="datetime-local"
                                                                [ngModel]="bucketBoundInput(bucket.min)"
                                                                (ngModelChange)="
                                                                    updateActiveBucket(bucketIndex, {
                                                                        min: parseBucketBound($event),
                                                                    })
                                                                "
                                                            />
                                                        } @else {
                                                            <input
                                                                class="c-input"
                                                                type="time"
                                                                [ngModel]="bucketBoundInput(bucket.min)"
                                                                (ngModelChange)="
                                                                    updateActiveBucket(bucketIndex, {
                                                                        min: parseBucketBound($event),
                                                                    })
                                                                "
                                                            />
                                                        }
                                                    </label>
                                                    <label class="se-analytics__bucket-field">
                                                        <span>Max</span>
                                                        @if (activeBucketKind() === "numeric") {
                                                            <input
                                                                class="c-input"
                                                                type="number"
                                                                step="any"
                                                                [ngModel]="bucket.max"
                                                                (ngModelChange)="
                                                                    updateActiveBucket(bucketIndex, {
                                                                        max: parseBucketBound($event),
                                                                    })
                                                                "
                                                            />
                                                        } @else if (activeBucketKind() === "date") {
                                                            <input
                                                                class="c-input"
                                                                type="date"
                                                                [ngModel]="bucketBoundInput(bucket.max)"
                                                                (ngModelChange)="
                                                                    updateActiveBucket(bucketIndex, {
                                                                        max: parseBucketBound($event),
                                                                    })
                                                                "
                                                            />
                                                        } @else if (activeBucketKind() === "datetime") {
                                                            <input
                                                                class="c-input"
                                                                type="datetime-local"
                                                                [ngModel]="bucketBoundInput(bucket.max)"
                                                                (ngModelChange)="
                                                                    updateActiveBucket(bucketIndex, {
                                                                        max: parseBucketBound($event),
                                                                    })
                                                                "
                                                            />
                                                        } @else {
                                                            <input
                                                                class="c-input"
                                                                type="time"
                                                                [ngModel]="bucketBoundInput(bucket.max)"
                                                                (ngModelChange)="
                                                                    updateActiveBucket(bucketIndex, {
                                                                        max: parseBucketBound($event),
                                                                    })
                                                                "
                                                            />
                                                        }
                                                    </label>
                                                    <button
                                                        type="button"
                                                        class="c-textButton se-analytics__bucket-remove"
                                                        (click)="removeBucket(bucketIndex)"
                                                        aria-label="Remove range"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            }
                                        </div>
                                        <div class="se-analytics__bucket-actions">
                                            <button type="button" class="c-textButton" (click)="addBucket()">
                                                Add range
                                            </button>
                                            <button type="button" class="c-textButton" (click)="suggestBuckets()">
                                                Suggest equal ranges (5)
                                            </button>
                                            @if (activeDemographicBuckets().length) {
                                                <button
                                                    type="button"
                                                    class="c-textButton"
                                                    (click)="clearBucketsForActiveCa()"
                                                >
                                                    Clear ranges
                                                </button>
                                            }
                                        </div>
                                    </details>
                                }
                                <div class="se-analytics__chart" echarts [options]="demographicOption()"></div>
                            } @else {
                                <p class="se-analytics__muted">Select one or more contact attributes above.</p>
                            }
                        } @else {
                            <div class="se-analytics__chart" echarts [options]="optionFor(key)"></div>
                        }
                    </section>
                }
            }
        </div>
    `,
    styles: [
        `
            .se-analytics__title {
                margin: 0 0 1rem;
            }
            .se-analytics__controls {
                display: flex;
                flex-wrap: wrap;
                gap: 1rem;
                align-items: flex-end;
                margin-bottom: 1rem;
            }
            .se-analytics__field {
                display: flex;
                flex-direction: column;
                gap: 0.4rem;
            }
            .se-analytics__field span {
                font-size: 0.875rem;
            }
            .se-analytics__field--checkbox {
                flex-direction: row;
                align-items: center;
                gap: 0.35rem;
                align-self: end;
                padding-bottom: 0.35rem;
            }
            .se-analytics__field--checkbox span {
                white-space: nowrap;
            }
            .se-analytics__actions {
                display: flex;
                gap: 0.5rem;
                align-items: center;
            }
            .se-analytics__filters {
                display: flex;
                flex-wrap: wrap;
                gap: 1rem;
                align-items: flex-end;
                margin-bottom: 1rem;
            }
            .se-analytics__field--wide {
                min-width: 14rem;
            }
            .se-analytics__demographics-panel {
                margin-bottom: 1rem;
                border: 1px solid #ddd;
                border-radius: 6px;
                padding: 0.5rem 0.75rem;
            }
            .se-analytics__demographics-summary {
                cursor: pointer;
                font-weight: 600;
                list-style-position: inside;
            }
            .se-analytics__demographics-count {
                margin-left: 0.5rem;
                font-weight: 400;
                color: #666;
            }
            .se-analytics__demographics-options {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
                gap: 0.5rem 1rem;
                margin-top: 0.75rem;
                max-height: 12rem;
                overflow-y: auto;
                padding-right: 0.25rem;
            }
            .se-analytics__demographics-option {
                display: flex;
                gap: 0.35rem;
                align-items: flex-start;
                line-height: 1.3;
            }
            .se-analytics__demographics-option input[type="checkbox"].c-input {
                height: 1em;
                width: 1em;
                min-height: 0;
                padding: 0;
                margin: 0.15em 0 0;
                flex: 0 0 auto;
            }
            .se-analytics__demographics-empty {
                margin: 0.5rem 0 0;
            }
            .se-analytics__widget-toggles {
                display: flex;
                flex-wrap: wrap;
                gap: 0.75rem;
                align-items: center;
                margin-bottom: 1rem;
            }
            .se-analytics__chip {
                display: inline-flex;
                gap: 0.35rem;
                align-items: center;
            }
            .se-analytics__widget {
                border: 1px solid #ddd;
                border-radius: 6px;
                padding: 1rem;
                margin-bottom: 1rem;
            }
            .se-analytics__widget-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 0.5rem;
            }
            .se-analytics__widget-header h3 {
                margin: 0;
            }
            .se-analytics__widget-actions {
                display: flex;
                gap: 0.5rem;
            }
            .se-analytics__chart {
                width: 100%;
                height: 360px;
            }
            .se-analytics__cards {
                display: flex;
                flex-wrap: wrap;
                gap: 1rem;
            }
            .se-analytics__card {
                flex: 1 1 120px;
                text-align: center;
                padding: 1rem;
                background: #f5f7fa;
                border-radius: 6px;
            }
            .se-analytics__card-value {
                font-size: 1.75rem;
                font-weight: 700;
            }
            .se-analytics__card-label {
                font-size: 0.85rem;
                color: #666;
            }
            .se-analytics__dimensions {
                display: flex;
                flex-wrap: wrap;
                gap: 0.5rem;
                margin-bottom: 0.5rem;
            }
            .se-analytics__dimension {
                padding: 0.25rem 0.75rem;
                border: 1px solid #ccc;
                background: #fff;
                border-radius: 4px;
                cursor: pointer;
            }
            .se-analytics__dimension--active {
                border-color: #1976d2;
                background: #e3f2fd;
                font-weight: 600;
            }
            .se-analytics__muted {
                color: #666;
            }
            .se-analytics__error {
                color: #c62828;
            }
            .se-analytics__bucket-panel {
                margin-bottom: 0.75rem;
                border: 1px solid #ddd;
                border-radius: 6px;
                padding: 0.5rem 0.75rem;
            }
            .se-analytics__bucket-summary {
                cursor: pointer;
                font-weight: 600;
                list-style-position: inside;
            }
            .se-analytics__bucket-hint {
                margin: 0.5rem 0;
                font-size: 0.85rem;
            }
            .se-analytics__bucket-rows {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                margin-bottom: 0.5rem;
            }
            .se-analytics__bucket-row {
                display: flex;
                flex-wrap: wrap;
                gap: 0.5rem;
                align-items: flex-end;
            }
            .se-analytics__bucket-field {
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
                min-width: 6rem;
                flex: 1 1 6rem;
            }
            .se-analytics__bucket-field span {
                font-size: 0.8rem;
            }
            .se-analytics__bucket-remove {
                margin-bottom: 0.25rem;
            }
            .se-analytics__bucket-actions {
                display: flex;
                flex-wrap: wrap;
                gap: 0.75rem;
            }
        `,
    ],
})
export class SeAnalyticsDashboardComponent {
    readonly allWidgets = ALL_WIDGETS;

    // One-way URL state: read the query string ONCE at construction to seed the controls; never again.
    private readonly seed = readSeed();

    readonly startDt = signal<string>(this.seed.get("dateStart") || isoDate(this.defaultStart()));
    readonly endDt = signal<string>(this.seed.get("dateEnd") || isoDate(new Date()));
    readonly bucket = signal<Bucket>(this.seedBucket());
    readonly metric = signal<Metric>(this.seed.get("metric") === "unique" ? "unique" : "total");
    readonly excludeNone = signal(this.seedExcludeNone());
    readonly selectedCaIds = signal<number[]>(this.seedIds("ca"));
    readonly activeDemographic = signal<number | null>(null);
    readonly demographicBucketsByCaId = signal<Record<number, DemographicBucketDef[]>>({});

    // Committed picker selections (source of truth for the id filters). Seeded from the URL once,
    // then updated when a picker emits (done). Each item-store picker handles its own search/paging.
    readonly orgsModel = signal<DropDownItem[]>(this.seedItems("orgs"));
    readonly eventCategoriesModel = signal<DropDownItem[]>(this.seedItems("cats"));
    readonly eventsModel = signal<DropDownItem[]>(this.seedItems("events"));
    readonly locationsModel = signal<DropDownItem[]>(this.seedItems("locs"));

    readonly organizationIds = computed(() => idsOf(this.orgsModel()));
    readonly categoryIds = computed(() => idsOf(this.eventCategoriesModel()));
    readonly eventIds = computed(() => idsOf(this.eventsModel()));
    readonly locationRoomIds = computed(() => idsOf(this.locationsModel()));

    // Bumped on Clear Filters to force the init-only item-store pickers to remount with empty selections.
    readonly pickersRemountKey = signal(0);

    readonly contactCasRes = s25Resource({
        loader: async () => {
            const response = await CustomAttributeService.getCustomAttributes(Item.Ids.Contact, false);
            const attributes = (response?.custom_attributes?.attribute as any[]) ?? [];
            return attributes.map(
                (attribute): ContactCustomAttributeDef => ({
                    custAtrbId: parseInt(attribute.cust_atrb_id, 10),
                    custAtrbName: attribute.cust_atrb_name,
                    atrbDataType: attribute.cust_atrb_type,
                }),
            );
        },
    });

    readonly filter = computed<EngagementAnalyticsFilter>(() => ({
        dateStart: this.startDt() ? `${this.startDt()}T00:00:00` : undefined,
        dateEnd: this.endDt() ? `${this.endDt()}T23:59:59` : undefined,
        bucket: this.bucket(),
        organizationIds: this.organizationIds(),
        categoryIds: this.categoryIds(),
        eventIds: this.eventIds(),
        locationRoomIds: this.locationRoomIds(),
        demographicCustAtrbIds: this.selectedCaIds(),
        excludeNone: this.excludeNone() || undefined,
        topN: 10,
    }));

    readonly summaryRes = s25Resource({
        params: () => this.filter(),
        loader: ({ params }) => SeAnalyticsService.getSummary(params),
    });

    readonly loading = computed(() => this.summaryRes.isLoading());

    readonly selectedCaCount = computed(() => this.selectedCaIds().length);

    readonly errorMessage = computed(() => {
        const error = this.summaryRes.error();
        if (!error) return undefined;
        return error instanceof Error ? error.message : String(error);
    });

    readonly summary = computed(
        () =>
            this.summaryRes.value()?.summary ?? {
                activeOrganizations: 0,
                attendedEvents: 0,
                totalAttendees: 0,
                uniqueAttendees: 0,
                averageAttendees: 0,
            },
    );

    readonly demographics = computed<DemographicBreakdown[]>(() => this.summaryRes.value()?.demographics ?? []);

    readonly activeDemographicBreakdown = computed<DemographicBreakdown | undefined>(() => {
        const dims = this.demographics();
        const active = this.activeDemographic() ?? dims[0]?.custAtrbId ?? null;
        return dims.find((entry) => entry.custAtrbId === active) ?? dims[0];
    });

    readonly activeCaDataType = computed(() => {
        const activeId = this.activeDemographicBreakdown()?.custAtrbId;
        if (activeId == null) {
            return undefined;
        }
        return this.contactCasRes.value()?.find((ca) => ca.custAtrbId === activeId)?.atrbDataType;
    });

    readonly activeBucketKind = computed(() => getDemographicBucketKind(this.activeCaDataType()));

    readonly showBucketizer = computed(
        () => isBucketizableDemographicType(this.activeCaDataType()) && !!this.activeDemographicBreakdown(),
    );

    readonly activeDemographicBuckets = computed(() => {
        const activeId = this.activeDemographicBreakdown()?.custAtrbId;
        if (activeId == null) {
            return [];
        }
        return this.demographicBucketsByCaId()[activeId] ?? [];
    });

    readonly activeBucketValidationError = computed(() => validateBuckets(this.activeDemographicBuckets()));

    readonly bucketRangeHintText = computed(() => {
        const kind = this.activeBucketKind();
        return kind ? bucketRangeHint(kind) : "";
    });

    readonly useBucketsForActiveCa = computed(
        () =>
            this.showBucketizer() &&
            hasValidBucketLabels(this.activeDemographicBuckets()) &&
            !this.activeBucketValidationError(),
    );

    readonly showSuggestBucketsPrompt = computed(() => {
        const kind = this.activeBucketKind();
        const slices = this.activeDemographicBreakdown()?.slices ?? [];
        return (
            this.showBucketizer() &&
            !!kind &&
            !hasValidBucketLabels(this.activeDemographicBuckets()) &&
            hasHighCardinality(slices, kind)
        );
    });

    // Persisted per-user widget show/hide + order.
    readonly widgetPrefRes = s25Resource({
        loader: () => PreferenceService.getPreferences([WIDGET_PREF], "U"),
    });

    readonly widgetConfig = signal<WidgetConfig>(this.defaultWidgetConfig());

    readonly visibleWidgets = computed(() =>
        this.widgetConfig().order.filter((key) => !this.widgetConfig().hidden[key]),
    );

    readonly demographicOption = computed<EChartsOption>(() => {
        const dim = this.activeDemographicBreakdown();
        if (!dim) {
            return { title: { text: "No data", left: "center", top: "middle" } };
        }

        const bucketKind = this.activeBucketKind();
        const slices =
            this.useBucketsForActiveCa() && bucketKind
                ? aggregateSlicesIntoBuckets(dim.slices, this.activeDemographicBuckets(), bucketKind)
                : dim.slices.map((slice) => ({ name: slice.value, total: slice.total, unique: slice.unique }));

        return pieOption(slices, this.metric());
    });

    constructor() {
        // Seed the persisted widget config once the preference resolves.
        effect(() => {
            const raw = this.widgetPrefRes.value()?.[WIDGET_PREF]?.value;
            const parsed = this.parseWidgetConfig(raw);
            if (parsed) {
                this.widgetConfig.set(parsed);
            }
        });

        // One-way deep links: mirror the current filter to the query string so the view can be shared
        // and restored. The `seEngagementAnalytics` ui-router state sets `reloadOnSearch: false`, so these
        // writes do not reload/remount the view. We read the URL only once at construction (see seed*).
        effect(() => {
            this.syncUrl();
        });
    }

    optionFor(key: string): EChartsOption {
        const data = this.summaryRes.value();
        switch (key) {
            case "timeline":
                return timelineOption(data?.timeline ?? []);
            case "topOrganizations":
                return rankedBarOption(data?.topOrganizations ?? [], this.metric());
            case "topEvents":
                return rankedBarOption(data?.topEvents ?? [], this.metric());
            case "topLocations":
                return rankedBarOption(data?.topLocations ?? [], this.metric());
            case "categories":
                return pieOption(this.categorySlices(data?.categories ?? []), this.metric());
            case "membershipOverTime":
                return membershipOverTimeOption(data?.membershipOverTime ?? []);
            case "membership":
                return pieOption(
                    this.membershipSlices(
                        data?.membership ?? {
                            memberTotal: 0,
                            memberUnique: 0,
                            nonMemberTotal: 0,
                            nonMemberUnique: 0,
                        },
                    ),
                    this.metric(),
                );
            default:
                return { title: { text: "", left: "center", top: "middle" } };
        }
    }

    titleFor(key: string): string {
        return ALL_WIDGETS.find((widget) => widget.key === key)?.title ?? key;
    }

    isHidden(key: string): boolean {
        return !!this.widgetConfig().hidden[key];
    }

    isCaSelected(custAtrbId: number): boolean {
        return this.selectedCaIds().includes(custAtrbId);
    }

    onBucketChange(value: string): void {
        if (value === "day" || value === "week" || value === "month") {
            this.bucket.set(value);
        }
    }

    onMetricChange(value: string): void {
        this.metric.set(value === "unique" ? "unique" : "total");
    }

    onExcludeNoneChange(checked: boolean): void {
        this.excludeNone.set(checked);
    }

    toggleCa(custAtrbId: number): void {
        const current = this.selectedCaIds();
        this.selectedCaIds.set(
            current.includes(custAtrbId) ? current.filter((id) => id !== custAtrbId) : [...current, custAtrbId],
        );
    }

    bucketBoundInput(value: number | null): string {
        const kind = this.activeBucketKind();
        if (!kind || kind === "numeric") {
            return value === null ? "" : String(value);
        }
        return comparableToInputValue(value, kind);
    }

    parseBucketBound(raw: string | number | null): number | null {
        const kind = this.activeBucketKind();
        if (!kind) {
            return null;
        }
        return parseBucketInputValue(raw, kind);
    }

    addBucket(): void {
        this.updateBucketsForActiveCa((buckets) => [...buckets, emptyBucketDef()]);
    }

    removeBucket(index: number): void {
        this.updateBucketsForActiveCa((buckets) => buckets.filter((_, bucketIndex) => bucketIndex !== index));
    }

    updateActiveBucket(index: number, patch: Partial<DemographicBucketDef>): void {
        this.updateBucketsForActiveCa((buckets) =>
            buckets.map((bucket, bucketIndex) => (bucketIndex === index ? { ...bucket, ...patch } : bucket)),
        );
    }

    suggestBuckets(): void {
        const dim = this.activeDemographicBreakdown();
        const kind = this.activeBucketKind();
        if (!dim || !kind) {
            return;
        }
        this.setBucketsForActiveCa(suggestEqualBuckets(dim.slices, kind));
    }

    clearBucketsForActiveCa(): void {
        this.setBucketsForActiveCa([]);
    }

    toggleWidget(key: string): void {
        const config = this.cloneConfig();
        config.hidden[key] = !config.hidden[key];
        this.commitConfig(config);
    }

    moveUp(key: string): void {
        this.move(key, -1);
    }

    moveDown(key: string): void {
        this.move(key, 1);
    }

    reload(): void {
        this.summaryRes.resource.reload();
    }

    async exportReport(reportKey: string): Promise<void> {
        try {
            const csv = await SeAnalyticsService.exportCsv({ ...this.filter(), reportKey });
            this.downloadCsv(csv, `engagement_${reportKey}.csv`);
        } catch (caughtError) {
            S25Util.showError(caughtError, "Failed to export report");
        }
    }

    private move(key: string, delta: number): void {
        const config = this.cloneConfig();
        const index = config.order.indexOf(key);
        const target = index + delta;
        if (index < 0 || target < 0 || target >= config.order.length) {
            return;
        }
        const [moved] = config.order.splice(index, 1);
        config.order.splice(target, 0, moved);
        this.commitConfig(config);
    }

    private commitConfig(config: WidgetConfig): void {
        this.widgetConfig.set(config);
        PreferenceService.setPreference(WIDGET_PREF, JSON.stringify(config), "U").catch((caughtError) =>
            S25Util.showError(caughtError, "Failed to save layout"),
        );
    }

    private cloneConfig(): WidgetConfig {
        const current = this.widgetConfig();
        return { order: [...current.order], hidden: { ...current.hidden } };
    }

    private categorySlices(categories: CategorySlice[]): { name: string; total: number; unique: number }[] {
        return categories.map((category) => ({
            name: category.name ?? `#${category.catId ?? "?"}`,
            total: category.total,
            unique: category.unique,
        }));
    }

    private membershipSlices(membership: MembershipBreakdown): { name: string; total: number; unique: number }[] {
        return [
            { name: "Members", total: membership.memberTotal, unique: membership.memberUnique },
            { name: "Non-members", total: membership.nonMemberTotal, unique: membership.nonMemberUnique },
        ];
    }

    private downloadCsv(csv: string, fileName: string): void {
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
    }

    /**
     * One-way write of the current filter to the ui-router query string for shareable deep links. Goes
     * through $location (router-aware) so it does not fight the AngularJS digest, and the state declares
     * these params as reloadOnSearch:false (dynamic) so the write never reloads/remounts the view.
     */
    private syncUrl(): void {
        StateService.setSearch({
            dateStart: this.startDt(),
            dateEnd: this.endDt(),
            bucket: this.bucket(),
            metric: this.metric(),
            excludeNone: this.excludeNone() ? "1" : undefined,
            ca: idsToParam(this.selectedCaIds()),
            orgs: idsToParam(this.organizationIds()),
            cats: idsToParam(this.categoryIds()),
            events: idsToParam(this.eventIds()),
            locs: idsToParam(this.locationRoomIds()),
        });
    }

    private defaultStart(): Date {
        const date = new Date();
        date.setMonth(date.getMonth() - 3);
        return date;
    }

    private seedBucket(): Bucket {
        const value = this.seed.get("bucket");
        return value === "day" || value === "week" || value === "month" ? value : "month";
    }

    private seedExcludeNone(): boolean {
        const value = this.seed.get("excludeNone");
        return value === "1" || value === "true";
    }

    clearFilters(): void {
        this.startDt.set(isoDate(this.defaultStart()));
        this.endDt.set(isoDate(new Date()));
        this.bucket.set("month");
        this.metric.set("total");
        this.excludeNone.set(false);
        this.selectedCaIds.set([]);
        this.activeDemographic.set(null);
        this.demographicBucketsByCaId.set({});
        this.orgsModel.set([]);
        this.eventCategoriesModel.set([]);
        this.eventsModel.set([]);
        this.locationsModel.set([]);
        // Remount the pickers so their internal selection chips clear (they read value only at init).
        this.pickersRemountKey.update((key) => key + 1);
    }

    commitPicker(kind: PickerKind, items: Nullable<DropDownItem[]>): void {
        const selected = items ?? [];
        switch (kind) {
            case "orgs":
                this.orgsModel.set(selected);
                break;
            case "cats":
                this.eventCategoriesModel.set(selected);
                break;
            case "events":
                this.eventsModel.set(selected);
                break;
            case "locs":
                this.locationsModel.set(selected);
                break;
            default:
                S25Assert.never(kind, `Unknown picker kind: ${kind}`);
        }
    }

    private seedIds(param: string): number[] {
        const raw = this.seed.get(param);
        if (!raw) return [];
        return raw
            .split(",")
            .map((token) => parseInt(token.trim(), 10))
            .filter((id) => !isNaN(id));
    }

    // Deep-link rehydrate: seed each picker with bare {itemId} so the id filters apply immediately;
    // the item-store picker fills in display names as it loads/matches its options.
    private seedItems(param: string): DropDownItem[] {
        return this.seedIds(param).map((itemId) => ({ itemId }));
    }

    private updateBucketsForActiveCa(updater: (buckets: DemographicBucketDef[]) => DemographicBucketDef[]): void {
        const activeId = this.activeDemographicBreakdown()?.custAtrbId;
        if (activeId == null) {
            return;
        }
        const current = this.demographicBucketsByCaId();
        const nextBuckets = updater([...(current[activeId] ?? [])]);
        this.demographicBucketsByCaId.set({ ...current, [activeId]: nextBuckets });
    }

    private setBucketsForActiveCa(buckets: DemographicBucketDef[]): void {
        const activeId = this.activeDemographicBreakdown()?.custAtrbId;
        if (activeId == null) {
            return;
        }
        const current = this.demographicBucketsByCaId();
        this.demographicBucketsByCaId.set({ ...current, [activeId]: buckets });
    }

    private defaultWidgetConfig(): WidgetConfig {
        return { order: ALL_WIDGETS.map((widget) => widget.key), hidden: {} };
    }

    private parseWidgetConfig(raw: unknown): WidgetConfig | null {
        if (typeof raw !== "string" || !raw.trim()) {
            return null;
        }
        try {
            const parsed = JSON.parse(raw) as Partial<WidgetConfig>;
            const known = new Set(ALL_WIDGETS.map((widget) => widget.key));
            const order = (parsed.order ?? []).filter((key) => known.has(key));
            // Append any widgets added since the preference was saved so new features still show.
            for (const widget of ALL_WIDGETS) {
                if (!order.includes(widget.key)) {
                    order.push(widget.key);
                }
            }
            return { order, hidden: parsed.hidden ?? {} };
        } catch {
            return null;
        }
    }
}
